import { html, css, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import * as d3 from 'd3';
import { library, dom } from '@fortawesome/fontawesome-svg-core';
import {
  faCrosshairs,
  faUserFriends,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { abbreviateNumber } from '@aptitude/shared-vanilla';

import { svgWithJs, variables } from '../variables';

export type DonutIcons = '' | 'participants' | 'individual';
@customElement('apt-donut-chart')
export class DonutChart extends LitElement {
  @property({ type: Number }) width = 350;
  @property({ type: Number }) height = 130;
  @property({ type: Array }) data = [['', 0]];
  @property({ type: Array }) colorsRange = [''];
  @property({ type: Boolean }) isLinkRequired = true;
  @property({ type: String }) middleText = '';
  @property({ type: Number }) thickness = 0;
  @property({ type: Boolean }) emitRouteEvent = false;
  @property({ type: String }) strokeColor = 'white';
  @property({ type: Number }) strokeWidth = 1;
  @property({ type: String })
  icon: DonutIcons = '';

  DARK_GRAY = '#4a4a4a';
  ICON_SCALE = 0.004;

  allEntriesHaveZeroValue = false;
  static override get styles() {
    return [
      variables,
      svgWithJs,
      css`
        text a {
          font-family: var(--font-family-sans-serif);
          text-decoration: underline;
          font-weight: 400;
          font-size: 14px;
          fill: var(--gray-900);
        }
        .pie text {
          font-family: var(--font-family-sans-serif);
          font-size: 10px;
          fill: var(--gray-900);
        }
        .link {
          cursor: pointer;
          text-decoration: underline;
        }
      `,
    ];
  }

  constructor() {
    super();
    library.add(faCrosshairs, faUserFriends, faUser);
  }
  override render() {
    this.allEntriesHaveZeroValue = !this.data.find((x: any[]) => x[1] > 0);
    this.colorsRange = this.colorsRange.map((color) => `var(--${color})`);
    this.generateChart();
    return html`<div></div>`;
  }

  override firstUpdated(): void {
    // @ts-expect-error The dom.watch() TS definition indicates no parameters for this. However,
    // when you actually dig into the code, you see that it looks at the arguments after it gets
    // into the code and tries to pull some out if there aren't any (it even supplies an empty
    // object for options if you don't supply one yourself).
    //
    // Note: We have to wait for the first update for the shadow DOM to even be created. Starting
    // the watch now will cause the <i> we inserted to be replaced with an SVG symbol we can reference
    // from out D3.js code.
    dom.watch({
      autoReplaceSvgRoot: this.shadowRoot,
      observeMutationsRoot: this.shadowRoot,
    });
  }

  private generateChart() {
    if (this.shadowRoot) {
      const domContainer = this.createContainer();
      if (this.isLinkRequired) {
        this.generateLabels(domContainer);
      }
      this.drawDonut(domContainer);
    }
  }
  private generateLabels(
    domContainer: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  ) {
    const color = d3.scaleOrdinal().range(this.colorsRange);

    // Add dots in legend
    domContainer
      .selectAll('dots')
      .data(this.data)
      .enter()
      .append('circle')
      .attr('cx', 155)
      .attr('cy', (d, i) => 40 + i * 35) // 40 is where the first dot appears. 35 is the distance between dots
      .attr('r', 5)
      .style('fill', this.getLabelColor(color));

    // Add links in legend
    if (this.emitRouteEvent) {
      domContainer
        .selectAll('links')
        .data(this.data)
        .enter()
        .append('text')
        .attr('x', 170)
        .attr('y', (d, i) => 43 + i * 35)
        .append('a')
        .attr('class', 'link')
        .on('click', (d, data) => this.handleClick(String(data[2]))) //d[2] equals url
        .text((d) => abbreviateNumber(+d[1]) + ' ' + d[0]); //d[0] equals label, d[1] equals value

      return;
    }

    domContainer
      .selectAll('links')
      .data(this.data)
      .enter()
      .append('text')
      .attr('x', 170)
      .attr('y', (d, i) => 43 + i * 35)
      .append('a')
      .attr('xlink:href', (d) => d[2]) //d[2] equals url
      .text((d) => abbreviateNumber(+d[1]) + ' ' + d[0]); //d[0] equals label, d[1] equals value
  }

  private handleClick(url: string) {
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { url },
      }),
    );
  }

  getLabelColor(color: d3.ScaleOrdinal<string, unknown, never>): any {
    return function (d: any) {
      return color(d);
    };
  }

  drawDonut(container: any) {
    const margin = 10;
    const tweenDuration = 600;
    let donutColorsRange = this.colorsRange;
    //show the donut with one single arc in light gray when all the input values are 0
    if (this.allEntriesHaveZeroValue) {
      this.data.push(['default', 1]);
      donutColorsRange = ['var(--gray-10)'];
    }

    const color = d3.scaleOrdinal().range(donutColorsRange);

    // Tween for animating chart.
    const arcTween = (d: {}, index: number) => {
      const that = this as unknown as { _current: unknown };
      const i = d3.interpolate(that._current, d);
      if (index === 1) {
        that._current = i(0);
      }
      return (t: number) => arc(i(t), index);
    };

    // The radius of the pieplot is half the width or half the height (smallest one). I subtract a bit of margin.
    const radius = Math.min(this.width, this.height) / 2 - margin;

    // Compute the position of each group on the pie:
    const pie = d3.pie().value((d: any) => d[1]);

    const innerRadius: number = this.thickness
      ? radius - Number(this.thickness)
      : 30;
    // const arc = d3.arc().innerRadius(radius - this.thickness).outerRadius(radius);
    const arc: any = d3
      .arc()
      .innerRadius(innerRadius) // This is the size of the donut hole
      .outerRadius(radius);

    const translateX = this.isLinkRequired ? 70 : this.height / 2;
    const translateY = this.isLinkRequired ? 70 : this.width / 2;
    const g = container
      .append('g')
      .attr('transform', `translate(${translateX}, ${translateY})`);

    // Build the pie chart: Basically, each part of the pie is a path that we build using the arc function.
    g.selectAll('path')
      .data(pie(this.data as any))
      .join('path')
      .attr('d', arc)
      .attr('stroke', this.strokeColor)
      .style('stroke-width', this.strokeWidth);

    // middle text if needed
    if (this.middleText) {
      g.attr('class', 'pie')
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '.35em')
        .attr('data-test-selector', 'middle-text')
        .text(this.middleText);
    }

    if (this.icon) {
      const iconScale = this.width * this.ICON_SCALE;
      const iconSize = iconScale * this.width;

      d3.select(this.shadowRoot as unknown as Element)
        .append('svg')
        .attr('width', this.width)
        .attr('height', this.height)
        .attr('transform', `translate(-${this.width}, 0)`)
        .attr('class', 'icon')
        .append('use')
        .attr('xlink:href', `#icon`)
        .attr(
          'transform',
          `translate(${translateX - iconSize / 2}, ${
            translateY - iconSize / 2
          }) scale(${iconScale}, ${iconScale})`,
        )
        .attr('fill', this.DARK_GRAY);
    }

    // TODO: This will redraw and animate. We should animate from current value.
    container
      .selectAll('path')
      .attr('fill', (d: { data: string[] }) => color(d.data[0]))
      .transition()
      .duration(tweenDuration)
      .attrTween('d', arcTween);
  }
  createContainer() {
    // This is the outermost container for the chart. Just in case we already have SVG elements out
    // there (say from a previous rendering), remove it first.
    d3.select(this.shadowRoot as unknown as Element)
      .selectChildren('svg#donutChart')
      .remove();

    const svg = d3
      .select(this.shadowRoot as unknown as Element)
      .append('svg')
      .attr('id', 'donutChart')
      .attr('width', this.width)
      .attr('height', this.height);

    const iconName = this.getIconName(this.icon);
    d3.select(this.shadowRoot as unknown as Element)
      .append('i')

      .attr('data-fa-symbol', 'icon')
      .attr('class', `fas ${iconName}`);

    return svg;
  }

  getIconName(icon: DonutIcons): string {
    switch (icon) {
      case 'participants':
        return 'fa-user-friends';
      case 'individual':
        return 'fa-user';
      default:
        return '';
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'apt-donut-chart': DonutChart;
  }
}
